import { sha1 } from 'js-sha1' // 同期処理にするために導入しています。ちゃんとしたhashが必要な場合はcrypto.subtleを使ってください。

export const assertNever = (value: never): never => {
  throw new Error(`Unexpected value: ${JSON.stringify(value)}`)
}

export const excludeNull = <T>(nullableArray: (T | null | undefined)[]): T[] => {
  return nullableArray.filter((v): v is T => v !== null && v !== undefined)
}

export type Loadable<T, TError = string> = {
  isLoading: boolean
  value?: T | undefined
  error?: TError | undefined
}
export const requestStatus = <T, TError = string>(request?: Loadable<T, TError>): 'initial' | 'requesting' | 'success' | 'error' => {
  if (!request) {
    return 'initial'
  }

  if (request.isLoading) {
    return 'requesting'
  } else if (!!request.error) {
    return 'error'
  } else if (!!request.value) {
    return 'success'
  } else {
    return 'initial'
  }
}
export const transformLoadable = <T, U, TError = string>(loadable: Loadable<T, TError>, transform: (value: T) => U): Loadable<U, TError> => {
  return {
    isLoading: loadable.isLoading,
    value: loadable.value !== undefined ? transform(loadable.value) : undefined,
    error: loadable.error,
  }
}

export const loadableErrors = <T>(loadables: Loadable<T, unknown>[]): unknown[] => {
  return loadables.filter((loadable) => !!loadable.error).map((loadable) => loadable.error!)
}

export const getFaviconUrl = (url: string): string => {
  return `https://www.google.com/s2/favicons?domain_url=${url}&sz=256`
}

export function stringToColorHex(str: string): string {
  const hexString = sha1.create().update(str).hex()

  // Convert the first 7 bytes to an integer for hue
  const hue = parseInt(hexString.substring(0, 14), 16) % 360

  // Convert the next 7 bytes to a value between 25% and 75% for lightness
  const lightness = 25 + (parseInt(hexString.substring(14, 28), 16) % 51)

  // Convert the next 6 bytes to a value between 25% and 75% for saturation
  const saturation = 25 + (parseInt(hexString.substring(28, 40), 16) % 51)

  // HSL to RGB conversion
  function hslToRgb(h: number, s: number, l: number): { r: number; g: number; b: number } {
    s /= 100
    l /= 100

    const c = (1 - Math.abs(2 * l - 1)) * s
    const x = c * (1 - Math.abs(((h / 60) % 2) - 1))
    const m = l - c / 2

    let r = 0,
      g = 0,
      b = 0

    if (0 <= h && h < 60) {
      r = c
      g = x
      b = 0
    } else if (60 <= h && h < 120) {
      r = x
      g = c
      b = 0
    } else if (120 <= h && h < 180) {
      r = 0
      g = c
      b = x
    } else if (180 <= h && h < 240) {
      r = 0
      g = x
      b = c
    } else if (240 <= h && h < 300) {
      r = x
      g = 0
      b = c
    } else if (300 <= h && h < 360) {
      r = c
      g = 0
      b = x
    }

    r = Math.round((r + m) * 255)
    g = Math.round((g + m) * 255)
    b = Math.round((b + m) * 255)

    return { r, g, b }
  }

  // Convert HSL to RGB
  const { r, g, b } = hslToRgb(hue, saturation, lightness)

  // Convert RGB to Hex
  const rgbToHex = (value: number) => {
    const hex = value.toString(16).padStart(2, '0')
    return hex
  }

  return `#${rgbToHex(r)}${rgbToHex(g)}${rgbToHex(b)}`
}

export function areArraysEqual<T>(a: T[], b: T[], comparison?: (a: T, b: T) => boolean): boolean {
  if (a === b) return true
  if (a.length !== b.length) return false

  return a.every((aItem, index) => {
    const bItem = b[index] as T // b[index] is always defined because of the length check
    return comparison ? comparison(aItem, bItem) : aItem === bItem
  })
}

export const allElementsEqual = <T>(array: T[]): boolean => {
  if (array.length === 0) {
    return true
  }
  return array.every((value, _, array) => value === array[0])
}

export const calculateAverage = (values: (number | null)[]): number | null => {
  const filteredValues = values.filter((value): value is number => value !== null)
  if (filteredValues.length === 0) {
    return null
  }
  return filteredValues.reduce((acc, value) => acc + value, 0) / filteredValues.length
}

// shadcn/ui utility for merging Tailwind CSS classes
import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}
